import React from "react";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import LeadForm from "../components/LeadForm";
import { Crown, Compass, GraduationCap, ShieldCheck, Globe2 } from "lucide-react";

const PILLARS = [
  {
    icon: Compass,
    title: "Vision",
    text: "Transform global education through technology and experiential learning — making world-class institutional infrastructure accessible across borders.",
  },
  {
    icon: GraduationCap,
    title: "Mission",
    text: "Enable institutions with scalable digital ecosystems, world-class academic events, and structured global exposure programs that produce measurable outcomes.",
  },
  {
    icon: Crown,
    title: "Approach",
    text: "Institutional-first. We partner with governments, councils, schools, and universities — and engineer with the rigor those relationships demand.",
  },
];

const STATS = [
  { k: "UAE", v: "Headquartered in Dubai" },
  { k: "5+", v: "Countries Engaged" },
  { k: "25+", v: "Institutions Served" },
  { k: "End", v: "to End Execution" },
];

export default function AboutPage() {
  return (
    <PageWrapper testId="about-page">
      <PageHero
        eyebrow="About"
        title="A global institutional partner, engineered in the UAE."
        description="Destineze Educational Voyages FZCO operates at the intersection of education, technology, and global mobility — building the digital and experiential infrastructure of modern institutions."
        image="https://images.unsplash.com/photo-1751698186759-5bac83376c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMGFyY2hpdGVjdHVyZSUyMG1vZGVybiUyMGx1eHVyeXxlbnwwfHx8fDE3NzcyMjUwMzF8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Who We Are
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
              An institutional-first company. Not a vendor.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3 space-y-5 text-slate-600 leading-relaxed text-base sm:text-lg">
            <p>
              Destineze was founded to solve a structural problem: institutions
              are forced to assemble fragmented tools, vendors, and travel
              partners — and then carry the operational burden of stitching
              them into something coherent.
            </p>
            <p>
              We replace that fragmentation with architecture. Our work spans
              digital ecosystems, world-class academic events, structured
              global exposure programs, and end-to-end institutional travel —
              all under one institutional partner.
            </p>
            <p>
              We are headquartered in the UAE — one of the world's most
              connected education and policy hubs — and we engage clients
              across the Gulf, Asia, Europe, and Africa.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {PILLARS.map((p, i) => (
              <div
                key={p.title}
                data-testid={`about-pillar-${i}`}
                className="card-lift bg-white border border-slate-200 p-8 sm:p-10"
              >
                <div className="h-11 w-11 bg-navy text-gold flex items-center justify-center">
                  <p.icon size={20} strokeWidth={1.5} />
                </div>
                <div className="mt-7 text-[11px] uppercase tracking-[0.22em] text-gold-500">
                  0{i + 1}
                </div>
                <h3 className="mt-2 font-display text-2xl text-navy font-light">
                  {p.title}
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.v}>
                <div className="font-display text-5xl text-gold">{s.k}</div>
                <div className="mt-2 text-sm uppercase tracking-[0.2em] text-white/70">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 pt-10 border-t border-white/10">
            <div className="flex gap-4">
              <ShieldCheck className="text-gold" size={22} />
              <div>
                <div className="font-display text-lg">Discreet & secure</div>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">
                  Built for governance, continuity, and confidentiality at
                  institutional scale.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Globe2 className="text-gold" size={22} />
              <div>
                <div className="font-display text-lg">Globally fluent</div>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">
                  Designed and delivered with awareness of regional academic,
                  cultural, and policy nuances.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Crown className="text-gold" size={22} />
              <div>
                <div className="font-display text-lg">Premium by default</div>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">
                  Every engagement is treated like a flagship project — no
                  shortcuts, no operational drift.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Engage
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
              Speak to our institutional team.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We build long-term relationships with institutions, councils, and
              governments. Start with a structured conversation.
            </p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="general"
              testIdPrefix="about-lead-form"
              title="Start a conversation"
              subtitle="Share your context. We'll respond within one business day."
            />
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
